/* halloween.js — runtime for the Spookiton campaign sections.
 *
 * Classic script (defer), single IIFE, no module system — matches the theme's
 * own bespoke sections (rolife-9th-anniversary.js, craft-ti-test.js). The
 * theme's importmap is reserved for vendor/theme/photoswipe.
 *
 * Design rules carried over from the plan:
 *  - ONE passive scroll listener + ONE rAF pump for the whole page.
 *  - Every listener is registered against a per-root AbortController, so
 *    destroy() detaches everything in one abort() (theme-editor safe).
 *  - PROGRESSIVE ENHANCEMENT: Liquid renders the REVEALED state. This file
 *    *creates* the torch mask and *adds* the locked class. If the script never
 *    runs, the page is simply visible with no torch effect — never a black box.
 *  - Custom properties are hw- prefixed to match assets/halloween.css:
 *    --hw-mask-x, --hw-mask-y, --hw-progress.
 */
(function () {
  'use strict';

  if (window.Halloween) { window.Halloween.refresh(); return; }

  /* ---------------------------------------------------------------- config */
  var MOBILE = window.matchMedia('(max-width: 1180px)');
  var HERO_MOBILE = window.matchMedia('(max-aspect-ratio: 3/2)');
  var STACKED = window.matchMedia('(max-width: 1180px), (max-height: 700px)');
  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  var EDITING = !!(window.Shopify && window.Shopify.designMode);

  var MASK_DURATION = { hero: 1200, feature: 700, featureMobile: 700, tv: 600 };
  var MASK_HOME = { hero: [77, 62], feature: [64, 50], tv: [72, 66] };
  var GATE_WATCHDOG_SLACK_MS = 4000;   // grace on top of the dissolve duration
  var TOUCH_REVEAL_PX = 24;

  /* ------------------------------------------------------- shared measuring */
  var measureCanvas = document.createElement('canvas');
  var measureCtx = measureCanvas.getContext('2d');

  /* ----------------------------------------------------- shared scroll pump */
  var scrollSubs = new Set();
  var resizeSubs = new Set();
  var rafId = 0;
  var resizePending = false;

  function flush(now) {
    rafId = 0;
    if (resizePending) {
      resizePending = false;
      resizeSubs.forEach(function (fn) {
        try { fn(); } catch (err) { console.error('[halloween] resize sub failed', err); }
      });
    }
    scrollSubs.forEach(function (fn) {
      try { fn(now); } catch (err) { console.error('[halloween] scroll sub failed', err); }
    });
  }
  function pump() { if (!rafId) rafId = requestAnimationFrame(flush); }

  window.addEventListener('scroll', pump, { passive: true });
  window.addEventListener('resize', function () {
    resizePending = true;
    pump();
  });

  // A hidden page suspends rAF, so any scrolling that happened while the tab
  // was in the background never reached the subscribers. Re-sync on the way
  // back so a restored tab doesn't show a stale slide.
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) { resizePending = true; pump(); }
  });

  /* -------------------------------------------------------- scroll lock ---- */
  var lock = {
    depth: 0,
    prevGap: '',
    acquire: function () {
      if (this.depth++ > 0) return;
      var html = document.documentElement;
      this.prevGap = html.style.getPropertyValue('--hw-hero-scrollbar-gap');
      html.style.setProperty('--hw-hero-scrollbar-gap', Math.max(0, window.innerWidth - html.clientWidth) + 'px');
      html.classList.add('hw-hero-scroll-locked');
    },
    release: function () {
      if (this.depth === 0 || --this.depth > 0) return;
      var html = document.documentElement;
      html.classList.remove('hw-hero-scroll-locked');
      if (this.prevGap) html.style.setProperty('--hw-hero-scrollbar-gap', this.prevGap);
      else html.style.removeProperty('--hw-hero-scrollbar-gap');
      this.prevGap = '';
    },
    forceRelease: function () { if (this.depth > 0) { this.depth = 1; this.release(); } }
  };

  /* ============================================================== HwMask === */
  function HwMask(el, signal) {
    this.el = el;
    this.signal = signal;
    this.variant = el.getAttribute('data-hw-variant') || 'feature';
    this.gate = el.hasAttribute('data-hw-scroll-gate');
    this.revealed = false;
    this.dissolving = false;
    this.timer = 0;
    this.watchdog = 0;
    this.gateBound = false;
    this.heroPanel = el.closest('[data-hw-hero-panel]');

    this.duration = this.variant === 'hero' ? MASK_DURATION.hero
      : this.variant === 'tv' ? MASK_DURATION.tv
        : (MOBILE.matches ? MASK_DURATION.featureMobile : MASK_DURATION.feature);

    // Reduced motion, or the theme editor on a gated hero: stay revealed.
    // (A gate in the editor would make the section unscrollable for the merchant.)
    if (REDUCED.matches || (this.gate && EDITING)) return;

    this.build();
  }

  HwMask.prototype.build = function () {
    var self = this;
    var el = this.el;

    // build() runs again on every re-arm, so its listeners get their own
    // controller — otherwise pointermove/enter/leave would stack up one extra
    // copy per replay.
    if (this.buildAbort) this.buildAbort.abort();
    this.buildAbort = new AbortController();

    var mask = document.createElement('button');
    mask.className = 'hw-section-mask';
    mask.type = 'button';
    mask.setAttribute('aria-label', el.getAttribute('data-hw-mask-label') || 'Reveal');

    var cursor = document.createElement('span');
    cursor.className = 'hw-ignite-cursor';
    cursor.setAttribute('aria-hidden', 'true');
    var torch = el.getAttribute('data-hw-torch');
    if (torch) {
      var img = document.createElement('img');
      img.src = torch;
      img.alt = '';
      cursor.appendChild(img);
    }
    var label = document.createElement('b');
    label.appendChild(document.createTextNode('Leg'));
    var brk = document.createElement('span');
    brk.className = 'hw-mobile-ignite-break';
    brk.appendChild(document.createElement('br'));
    label.appendChild(brk);
    label.appendChild(document.createTextNode(' los'));
    cursor.appendChild(label);

    el.appendChild(mask);
    el.appendChild(cursor);
    this.mask = mask;
    this.cursor = cursor;

    el.classList.add('hw-is-locked');
    el.classList.remove('hw-is-revealed');

    // mobile torch home position
    if ((this.variant === 'hero' ? HERO_MOBILE : MOBILE).matches) {
      var home = MASK_HOME[this.variant] || MASK_HOME.feature;
      this.setLight(home[0], home[1]);
    } else {
      this.setLight(50, 50);
    }

    var opts = { signal: this.buildAbort.signal };
    if (this.variant === 'feature' || this.variant === 'tv') {
      MOBILE.addEventListener('change', function () {
        if (self.pointerFrame) cancelAnimationFrame(self.pointerFrame);
        self.pointerFrame = 0;
        self.lastPointer = null;
        self.pointer = null;
        if (self.revealed || self.dissolving) return;
        var home = self.isTorchFixed() ? MASK_HOME[self.variant] : [50, 50];
        self.setLight(home[0], home[1]);
        el.classList.remove('hw-is-active');
      }, opts);
    }
    mask.addEventListener('click', function () { self.reveal(); }, opts);
    el.addEventListener('pointermove', function (e) {
      if (self.isTorchFixed()) return;
      // Remembered even after the reveal (this listener outlives the mask), so
      // a restore can put the torch straight back under a mouse that never
      // moved — see rearm().
      self.lastPointer = { x: e.clientX, y: e.clientY, type: e.pointerType };
      // pointerenter is not the only way in: when the mask is rebuilt under a
      // pointer that is already inside, no enter event ever fires, so the first
      // movement has to switch the torch on as well.
      if (!self.dissolving && !self.revealed && !el.classList.contains('hw-is-active')) {
        el.classList.add('hw-is-active');
      }
      self.track(e);
    }, opts);
    el.addEventListener('pointerenter', function () {
      if (!self.dissolving && !self.revealed) el.classList.add('hw-is-active');
    }, opts);
    el.addEventListener('pointerleave', function () {
      el.classList.remove('hw-is-active');
    }, opts);

    if (this.gate) this.bindGate();
  };

  HwMask.prototype.setLight = function (x, y) {
    this.el.style.setProperty('--hw-mask-x', x + '%');
    this.el.style.setProperty('--hw-mask-y', y + '%');
  };

  HwMask.prototype.isTorchFixed = function () {
    return MOBILE.matches && (this.variant === 'feature' || this.variant === 'tv');
  };

  HwMask.prototype.track = function (event) {
    if (this.revealed || this.dissolving || this.isTorchFixed()) return;
    this.pointer = { x: event.clientX, y: event.clientY };
    if (this.pointerFrame) return;
    var self = this;
    this.pointerFrame = requestAnimationFrame(function () {
      self.pointerFrame = 0;
      if (self.revealed || self.dissolving || self.isTorchFixed()) return;
      var r = self.el.getBoundingClientRect();
      if (!r.width || !r.height) return;
      self.setLight(((self.pointer.x - r.left) / r.width) * 100,
        ((self.pointer.y - r.top) / r.height) * 100);
    });
  };

  /* ---- hero scroll gate ------------------------------------------------- */
  HwMask.prototype.bindGate = function () {
    if (this.gateBound) return;
    if (window.scrollY > 4 && !this.suppressUntil) { this.finish(); return; }
    var self = this, startY = null;
    this.gateAbort = new AbortController();
    this.gateBound = true;
    lock.acquire();
    this.gateLocked = true;
    var passive = { passive: true, signal: this.gateAbort.signal };
    var blocking = { passive: false, signal: this.gateAbort.signal };
    function eligible(target) {
      if (!self.gateBound || document.documentElement.classList.contains('lock')) return false;
      if (target && target.closest && target.closest('input, textarea, select, [contenteditable], [role="dialog"], cart-drawer, quick-buy-modal')) return false;
      return true;
    }
    window.addEventListener('wheel', function (e) {
      if (e.defaultPrevented || e.ctrlKey || !eligible(e.target)) return;
      if (e.cancelable) e.preventDefault();
      if (e.deltaY > 0 && Math.abs(e.deltaY) >= Math.abs(e.deltaX || 0)) self.reveal();
    }, blocking);
    window.addEventListener('touchstart', function (e) {
      startY = eligible(e.target) && e.touches.length === 1 ? e.touches[0].clientY : null;
    }, passive);
    window.addEventListener('touchmove', function (e) {
      if (!eligible(e.target) || startY === null || e.touches.length !== 1) return;
      if (e.cancelable) e.preventDefault();
      if (startY - e.touches[0].clientY > TOUCH_REVEAL_PX) self.reveal();
    }, blocking);
    window.addEventListener('keydown', function (e) {
      if (e.defaultPrevented || !eligible(e.target)) return;
      if (e.key === 'Escape') { self.finish(); return; }
      if (e.target && e.target.closest && e.target.closest('a, button') !== self.mask && e.target.closest('a, button')) return;
      if (['Enter', ' ', 'PageDown', 'ArrowDown', 'End'].indexOf(e.key) !== -1) {
        e.preventDefault();
        self.reveal();
      } else if (['PageUp', 'ArrowUp', 'Home'].indexOf(e.key) !== -1) e.preventDefault();
    }, { signal: this.gateAbort.signal });
    this.onGateScroll = function () {
      if (eligible(null) && window.scrollY > 4 && !self.dissolving && !self.suppressUntil) self.finish();
    };
    scrollSubs.add(this.onGateScroll);
  };

  HwMask.prototype.releaseGate = function () {
    this.gateBound = false;
    if (this.gateAbort) { this.gateAbort.abort(); this.gateAbort = null; }
    if (this.gateLocked) { lock.release(); this.gateLocked = false; }
    if (this.onGateScroll) {
      scrollSubs.delete(this.onGateScroll);
      this.onGateScroll = null;
    }
    if (this.watchdog) { clearTimeout(this.watchdog); this.watchdog = 0; }
  };

  /* ---- reveal ----------------------------------------------------------- */
  HwMask.prototype.reveal = function () {
    if (this.revealed || this.dissolving) return;
    if (this.suppressUntil && performance.now() < this.suppressUntil) {
      this.pendingReveal = true;
      return;
    }
    this.pendingReveal = false;
    var self = this;
    this.dissolving = true;
    this.el.classList.add('hw-is-dissolving');
    if (this.heroPanel) this.heroPanel.classList.add('hw-is-dissolving');

    // Failsafe: if finish() somehow never runs, the document would stay locked
    // forever. Armed only once a dissolve is actually in flight, never while
    // the visitor is simply looking at the first screen.
    if (this.gate) {
      this.watchdog = window.setTimeout(function () {
        if (!self.revealed) {
          console.warn('[halloween] dissolve watchdog fired — unlocking scroll');
          self.finish();
        }
      }, this.duration + GATE_WATCHDOG_SLACK_MS);
    }

    this.timer = window.setTimeout(function () { self.finish(); }, this.duration);
  };

  HwMask.prototype.finish = function () {
    if (this.revealed) return;
    this.revealed = true;
    this.revealedAt = performance.now();
    this.dissolving = false;
    if (this.timer) { clearTimeout(this.timer); this.timer = 0; }
    var el = this.el;
    el.classList.remove('hw-is-dissolving', 'hw-is-locked', 'hw-is-active');
    el.classList.add('hw-is-revealed');
    if (this.heroPanel) {
      this.heroPanel.classList.remove('hw-is-dissolving');
      this.heroPanel.classList.add('hw-is-revealed');
    }
    // Remove the overlay entirely (React unmounted it). Left in place, the
    // cursor:none mask keeps eating pointer events over the hotspots.
    if (this.mask && this.mask.parentNode) this.mask.parentNode.removeChild(this.mask);
    if (this.cursor && this.cursor.parentNode) this.cursor.parentNode.removeChild(this.cursor);
    this.mask = this.cursor = null;
    this.releaseGate();
    pump();                     // a quick return may have happened mid-reveal
  };

  HwMask.prototype.rearm = function (suppressMs) {
    if (REDUCED.matches || (this.gate && EDITING)) return;
    if (!this.revealed && !this.dissolving) return;   // already armed
    if (this.timer) { clearTimeout(this.timer); this.timer = 0; }
    this.suppressUntil = suppressMs ? performance.now() + suppressMs : 0;
    this.pendingReveal = false;
    this.revealed = false;
    this.dissolving = false;
    if (this.heroPanel) this.heroPanel.classList.remove('hw-is-revealed', 'hw-is-dissolving');
    this.build();

    // The torch is only visible with .hw-is-active, which pointerenter adds.
    // A restore is triggered by scrolling up with the mouse already resting on
    // the hero, so that enter never comes and the torch stayed invisible until
    // the visitor left the hero and came back. Put it back where the mouse is.
    var lp = this.lastPointer;
    if (lp && lp.type === 'mouse' && !this.isTorchFixed()) {
      var r = this.el.getBoundingClientRect();
      if (r.width && r.height && lp.x >= r.left && lp.x <= r.right && lp.y >= r.top && lp.y <= r.bottom) {
        this.setLight(((lp.x - r.left) / r.width) * 100, ((lp.y - r.top) / r.height) * 100);
        this.el.classList.add('hw-is-active');
      }
    }
  };

  HwMask.prototype.destroy = function () {
    if (this.pointerFrame) cancelAnimationFrame(this.pointerFrame);
    if (this.timer) clearTimeout(this.timer);
    if (this.buildAbort) { this.buildAbort.abort(); this.buildAbort = null; }
    this.releaseGate();
    var el = this.el;
    el.classList.remove('hw-is-locked', 'hw-is-dissolving', 'hw-is-active');
    el.classList.add('hw-is-revealed');
    if (this.mask && this.mask.parentNode) this.mask.parentNode.removeChild(this.mask);
    if (this.cursor && this.cursor.parentNode) this.cursor.parentNode.removeChild(this.cursor);
    if (this.heroPanel) this.heroPanel.classList.remove('hw-is-dissolving');
  };

  /* ========================================================== HwSequence === */
  function HwSequence(el, signal) {
    this.el = el;
    this.slides = Array.prototype.slice.call(el.querySelectorAll('[data-hw-slide]'));
    this.active = null;
    this.stacked = null;
    this.height = el.offsetHeight;
    this.stage = el.querySelector('.hw-workshop-sequence-stage');
    this.current = null;
    this.lastFrame = 0;
    this.lastPaint = null;
    this.layers = this.slides.map(function (slide) {
      return {
        copy: slide.querySelector('.hw-story-copy'),
        art: slide.querySelector('.hw-feature-mask-shell'),
        background: slide.querySelector('.hw-haunt-line')
      };
    });
    el.classList.add('hw-scroll-scenes');
    this.pinnedIndex = null;         // theme editor: force a slide
    var self = this;

    this.onScroll = function (now) { self.sync(now); };
    this.onResize = function () { self.height = el.offsetHeight; self.clearMotion(); self.current = null; self.sync(); };

    this.subscribe();
    STACKED.addEventListener('change', function () { self.subscribe(); self.onResize(); },
      { signal: signal });
    REDUCED.addEventListener('change', this.onResize, { signal: signal });
    this.sync();
  }

  HwSequence.prototype.subscribe = function () {
    // A stack stays readable on tablets and screens too short for a pinned scene.
    if (STACKED.matches) {
      scrollSubs.delete(this.onScroll);
      resizeSubs.delete(this.onResize);
    } else {
      scrollSubs.add(this.onScroll);
      resizeSubs.add(this.onResize);
    }
  };

  HwSequence.prototype.progress = function () {
    var range = Math.max(this.height - window.innerHeight, 1);
    var top = this.el.getBoundingClientRect().top;
    return Math.min(1, Math.max(0, -top / range));
  };

  HwSequence.prototype.sync = function (now) {
    if (STACKED.matches) {
      this.clearMotion();
      this.current = null;
      this.apply(this.pinnedIndex === null ? 0 : this.pinnedIndex);
      return;
    }
    var raw = this.progress();
    // Replace the whole scene in place; hysteresis prevents threshold chatter.
    var target = this.pinnedIndex === null
      ? (raw > 0.54 ? 1 : raw < 0.46 ? 0 : (this.target || 0))
      : this.pinnedIndex;
    this.target = target;
    if (REDUCED.matches) target = target < 0.5 ? 0 : 1;
    now = typeof now === 'number' ? now : performance.now();
    var dt = Math.min(64, Math.max(1, now - (this.lastFrame || now - 16.67)));
    this.lastFrame = now;
    if (this.current === null || REDUCED.matches || this.pinnedIndex !== null || raw === 0 || raw === 1) {
      this.current = target;
    } else {
      this.current += (target - this.current) * (1 - Math.exp(-dt / 70));
      if (Math.abs(target - this.current) < 0.0005) this.current = target;
    }
    this.paint(this.current);
    var active = this.current > 0.54 ? 1 : this.current < 0.46 ? 0 : (this.active || 0);
    this.apply(active);
    if (this.current !== target) pump();
  };

  HwSequence.prototype.paint = function (progress) {
    var value = Math.round(progress * 10000) / 10000;
    if (value === this.lastPaint) return;
    this.lastPaint = value;
    var eased = value * value * (3 - 2 * value);
    for (var i = 0; i < this.slides.length; i++) {
      var opacity = i === 0 ? 1 - eased : eased;
      var slide = this.slides[i];
      slide.style.opacity = opacity.toFixed(4);
      slide.style.visibility = opacity > 0 ? 'visible' : 'hidden';
      slide.style.transform = 'none';
    }
  };

  HwSequence.prototype.clearMotion = function () {
    if (this.lastPaint === null) return;
    this.lastPaint = null;
    for (var i = 0; i < this.slides.length; i++) {
      var slide = this.slides[i], layer = this.layers[i];
      slide.style.removeProperty('opacity');
      slide.style.removeProperty('visibility');
      slide.style.removeProperty('transform');
      [layer.copy, layer.art, layer.background].forEach(function (el) {
        if (el) el.style.removeProperty('transform');
      });
    }
  };

  HwSequence.prototype.apply = function (active) {
    var stacked = STACKED.matches;
    if (this.active === active && this.stacked === stacked) return;
    this.active = active;
    this.stacked = stacked;
    for (var i = 0; i < this.slides.length; i++) {
      var slide = this.slides[i];
      var isActive = i === active;
      slide.classList.toggle('hw-is-active', isActive);
      slide.classList.toggle('hw-is-exiting', !isActive && i < active);
      slide.classList.toggle('hw-is-entering', !isActive && i > active);
      slide.setAttribute('aria-hidden', stacked ? 'false' : String(!isActive));
      slide.inert = !stacked && !isActive;
    }
  };

  HwSequence.prototype.pin = function (index) {
    this.pinnedIndex = index;
    this.sync();
  };
  HwSequence.prototype.unpin = function () {
    this.pinnedIndex = null;
    this.sync();
  };

  HwSequence.prototype.destroy = function () {
    this.clearMotion();
    this.el.classList.remove('hw-scroll-scenes');
    scrollSubs.delete(this.onScroll);
    resizeSubs.delete(this.onResize);
  };

  /* ======================================================= HwHeroRestore === */
  /* Replays the whole intro when the visitor scrolls back to the very top:
   * the hero exit cascade runs in reverse and the scroll gate is re-armed, so the first screen is exactly as it was on load.
   *
   * The original is one-way (halloween-app-src.js:764 bails once isRevealed is
   * true, and nothing ever resets it). This is a deliberate, requested change.
   *
   * Collapsing again is NOT handled here — it is driven by the re-armed gate,
   * so the only way to dismiss the hero is scrolling down (or clicking the
   * torch), exactly like the first time.
   */
  var HERO_RESTORE_AT = 4;          // "back at the very top"
  var HERO_LEFT_TOP_AT = 120;       // "genuinely scrolled away from the top"
  var HERO_ANIM_MS = 1200;          // longest of the three (the person)
  var HERO_UP_INTENT_PX = 12;       // accumulated wheel-up before it counts
  var HERO_INTENT_GRACE_MS = 400;   // ignore gestures this soon after the reveal

  function HwHeroRestore(panel, mask, signal) {
    this.panel = panel;
    this.mask = mask;
    this.busy = false;
    this.timer = 0;
    // Latch: the visitor must actually leave the top before coming back can
    // count as a return. Without it the restore fires the instant finish()
    // releases the scroll lock, because the page is still sitting at scrollY 0
    // after being held there for the whole dissolve — the hero would pop back
    // and replay immediately.
    this.leftTop = false;
    var self = this;
    this.onScroll = function () { self.sync(); };
    scrollSubs.add(this.onScroll);

    // Upward intent AT the top. The scroll path above needs the page to
    // actually move — but right after the reveal the page is still sitting at
    // scrollY 0, so a wheel-up there moves nothing, fires no scroll event, and
    // the hero stayed collapsed until the visitor scrolled away and came back.
    // These listen to the gesture itself instead of its effect on scrollY.
    // Passive and never preventDefault(): they only observe.
    var opts = { passive: true, signal: signal };
    var upAccum = 0, lastWheel = 0, touchY = null;
    window.addEventListener('wheel', function (e) {
      var now = performance.now();
      if (e.deltaY >= 0 || now - lastWheel > 250) upAccum = 0;   // fresh gesture
      lastWheel = now;
      if (e.deltaY >= 0) return;
      upAccum += e.deltaY;
      if (upAccum <= -HERO_UP_INTENT_PX && self.canRestoreByIntent(e.target)) {
        upAccum = 0;
        self.restore();
      }
    }, opts);
    window.addEventListener('touchstart', function (e) {
      touchY = e.touches[0] ? e.touches[0].clientY : null;
    }, opts);
    window.addEventListener('touchmove', function (e) {
      if (touchY === null || !e.touches[0]) return;
      // finger moving DOWN the screen = asking to see what is above
      if (e.touches[0].clientY - touchY > TOUCH_REVEAL_PX && self.canRestoreByIntent(e.target)) {
        touchY = null;
        self.restore();
      }
    }, opts);
    window.addEventListener('keydown', function (e) {
      if ((e.key === 'ArrowUp' || e.key === 'PageUp' || e.key === 'Home') &&
          self.canRestoreByIntent(e.target)) self.restore();
    }, { signal: signal });
  }

  HwHeroRestore.prototype.sync = function () {
    if (this.busy || !this.mask) return;
    var y = window.scrollY;
    if (y > HERO_LEFT_TOP_AT) { this.leftTop = true; return; }
    if (this.mask.revealed && y <= HERO_RESTORE_AT && this.leftTop) this.restore();
  };

  // Is this gesture really "take me back to the first screen"?
  HwHeroRestore.prototype.canRestoreByIntent = function (target) {
    if (this.busy || !this.mask || !this.mask.revealed) return false;
    // Not at the top: it is ordinary page scrolling, handled by sync().
    if (window.scrollY > HERO_RESTORE_AT) return false;
    // The gesture that triggered the reveal can still be delivering events
    // (trackpad inertia, a bounce at the end of it) when finish() lands.
    if (performance.now() - (this.mask.revealedAt || 0) < HERO_INTENT_GRACE_MS) return false;
    // A drawer, modal or menu is open — the theme's layer lock puts .lock on
    // <html>. The gesture belongs to that layer, not to the page behind it.
    if (document.documentElement.classList.contains('lock')) return false;
    if (target && target.closest) {
      if (target.closest('input, textarea, select, [contenteditable]')) return false;
      // Scrolling inside something that can itself still scroll up (a drawer
      // body, a long list) is not a request to scroll the page.
      for (var n = target; n && n !== document.body && n.nodeType === 1; n = n.parentElement) {
        if (n.scrollTop > 0 && n.scrollHeight > n.clientHeight) return false;
      }
    }
    return true;
  };

  HwHeroRestore.prototype.restore = function () {
    var self = this;
    var cl = this.panel.classList;
    this.busy = true;
    this.leftTop = false;             // must leave again before the next replay
    if (this.timer) clearTimeout(this.timer);

    cl.remove('hw-is-revealed', 'hw-is-dissolving');   // unpin opacity/visibility
    cl.add('hw-is-restoring');                         // reverse cascade
    this.mask.rearm(HERO_ANIM_MS);

    this.timer = window.setTimeout(function () {
      self.panel.classList.remove('hw-is-restoring');
      if (self.mask) self.mask.suppressUntil = 0;
      self.busy = false;
      self.timer = 0;
      if (self.mask && (self.mask.pendingReveal || window.scrollY > HERO_RESTORE_AT)) self.mask.reveal();
    }, HERO_ANIM_MS);
  };

  HwHeroRestore.prototype.destroy = function () {
    scrollSubs.delete(this.onScroll);
    if (this.timer) clearTimeout(this.timer);
    this.panel.classList.remove('hw-is-restoring');
  };

  /* ========================================================== HwHeroFit === */
  /* Full-screen hero ("cover"). The panel is sized in CSS to
   * max(original height, 100svh - its own top), and the stage inside it keeps
   * the original box, so this only has to supply two numbers:
   *   --hw-hero-top  where the hero starts (announcement bar + header). Measured,
   *                  because the theme's own --announcement-bar-height is not
   *                  reliable on this page (it has read 441px for a 45px bar).
   *   --hw-hero-k    panel height / stage height, the uniform scale that makes
   *                  the stage fill the panel. Never below 1: on a short screen
   *                  the hero simply keeps its original size, as before.
   * The section also runs the same maths inline before first paint.
   */
  function HwHeroFit(panel, signal) {
    this.panel = panel;
    this.stage = panel.querySelector('[data-hw-hero-stage]');
    var self = this;
    this.onResize = function () { self.sync(); };
    resizeSubs.add(this.onResize);
    this.ro = null;
    if (this.stage && 'ResizeObserver' in window) {
      // The announcement bar and header can change height without a window
      // resize (a rotating announcement wrapping onto two lines, fonts loading).
      this.ro = new ResizeObserver(this.onResize);
      document.querySelectorAll('.shopify-section-group-header-group').forEach(function (el) {
        self.ro.observe(el);
      });
    }
    window.addEventListener('load', this.onResize, { once: true, signal: signal });
    this.sync();
  }

  HwHeroFit.prototype.sync = function () {
    var p = this.panel, stage = this.stage;
    if (!stage) return;
    var top = p.getBoundingClientRect().top + window.scrollY;
    p.style.setProperty('--hw-hero-top', Math.max(0, Math.round(top)) + 'px');
    var portrait = window.matchMedia('(max-aspect-ratio: 3/2)').matches;
    var artWidth = portrait ? 785 : 1440;
    var artHeight = portrait ? 1100 : 547;
    var width = p.clientWidth, height = p.clientHeight;
    var scale = Math.max(width / artWidth, height / artHeight);
    var paintedWidth = artWidth * scale, paintedHeight = artHeight * scale;
    p.style.setProperty('--hw-art-width', paintedWidth.toFixed(3) + 'px');
    p.style.setProperty('--hw-art-height', paintedHeight.toFixed(3) + 'px');
    p.style.setProperty('--hw-hero-k', '1');
    var visibleLeft = (paintedWidth - width) / paintedWidth * 50;
    p.style.setProperty('--hw-hero-vis-l', visibleLeft.toFixed(3) + '%');
    p.style.setProperty('--hw-hero-vis-r', (100 - visibleLeft).toFixed(3) + '%');
    if (!portrait) {
      p.querySelectorAll('.hw-hero-hotspot').forEach(function (hotspot, index) {
        var button = hotspot.querySelector('.hw-hero-hotspot-add');
        if (!button) return;
        var left = parseFloat(hotspot.style.left) / 100;
        var span = parseFloat(hotspot.style.width) / 100;
        var anchor = index === 0 ? .83 : parseFloat(button.style.left) / 100;
        if (!(span > 0) || !Number.isFinite(left + anchor)) return;
        var cropLeft = (paintedWidth - width) / 2;
        var x = (left + span * anchor) * paintedWidth;
        x = Math.max(cropLeft + 56, Math.min(cropLeft + width - 56, x));
        button.style.setProperty('--hw-marker-left', ((x / paintedWidth - left) / span * 100).toFixed(3) + '%');
      });
    }
  };

  HwHeroFit.prototype.destroy = function () {
    resizeSubs.delete(this.onResize);
    if (this.ro) this.ro.disconnect();
  };

  /* =========================================================== HwSpirits === */
  /* 13-card coverflow. Slot maths transcribed from getSpiritSlot
   * (halloween-app-src.js:317-339), wrap-around included. */
  function spiritSlot(i, active, n) {
    var o = active - i;
    if (o > n / 2) o -= n;
    else if (o < -n / 2) o += n;
    return o === -2 ? 'hw-is-far-right'
      : o === -1 ? 'hw-is-right'
        : o === 0 ? 'hw-is-center'
          : o === 1 ? 'hw-is-left'
            : o === 2 ? 'hw-is-far-left'
              : 'hw-is-hidden';
  }
  var SPIRIT_SLOTS = ['hw-is-far-left', 'hw-is-left', 'hw-is-center',
    'hw-is-right', 'hw-is-far-right', 'hw-is-hidden'];

  function HwSpirits(el, signal) {
    this.el = el;
    this.cards = Array.prototype.slice.call(el.querySelectorAll('.hw-spirit-card'));
    this.n = this.cards.length;
    this.active = 0;
    this.sliding = false;
    this.timer = 0;
    var self = this;
    var opts = { signal: signal };

    var prev = el.querySelector('.hw-arrow.hw-prev');
    var next = el.querySelector('.hw-arrow.hw-next');
    if (prev) prev.addEventListener('click', function () { self.slide(-1); }, opts);
    if (next) next.addEventListener('click', function () { self.slide(1); }, opts);

    // Touch: the original never calls preventDefault, and the CSS sets
    // touch-action: pan-y, so vertical scrolling must keep working.
    var startX = null, startY = null;
    el.addEventListener('touchstart', function (e) {
      startX = (e.touches[0] && e.touches[0].clientX);
      startY = (e.touches[0] && e.touches[0].clientY);
    }, { passive: true, signal: signal });
    el.addEventListener('touchend', function (e) {
      var endX = e.changedTouches[0] && e.changedTouches[0].clientX;
      var endY = e.changedTouches[0] && e.changedTouches[0].clientY;
      if (startX == null || endX === undefined) return;
      if (Math.abs(endX - startX) < 36 || Math.abs(endX - startX) <= Math.abs(endY - startY)) { startX = null; return; }
      self.slide(endX < startX ? 1 : -1);
      startX = null;
    }, { passive: true, signal: signal });

    // Keyboard support — the original had none.
    el.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') { self.slide(-1); e.preventDefault(); }
      else if (e.key === 'ArrowRight') { self.slide(1); e.preventDefault(); }
    }, opts);

    this.apply();
  }

  HwSpirits.prototype.slide = function (dir) {
    if (this.n === 0) return;
    if (this.sliding) { this.queuedDirection = dir; return; }
    var self = this;
    this.sliding = true;
    this.active = (this.active + dir + this.n) % this.n;
    this.apply();
    this.timer = window.setTimeout(function () {
      self.sliding = false;
      if (self.queuedDirection) {
        var queued = self.queuedDirection;
        self.queuedDirection = 0;
        self.slide(queued);
      }
    }, REDUCED.matches ? 0 : 480);
  };

  HwSpirits.prototype.goTo = function (index) {
    if (index < 0 || index >= this.n) return;
    this.active = index;
    this.apply();
  };

  // Rewrite EVERY card's slot class on every move — never just the ones that
  // changed. .hw-is-hidden carries transition:none, so a recycled card must
  // pass through it to teleport instead of flying across the stage.
  HwSpirits.prototype.apply = function () {
    for (var i = 0; i < this.n; i++) {
      var want = spiritSlot(i, this.active, this.n);
      var cl = this.cards[i].classList;
      for (var s = 0; s < SPIRIT_SLOTS.length; s++) {
        if (SPIRIT_SLOTS[s] !== want) cl.remove(SPIRIT_SLOTS[s]);
      }
      cl.add(want);
    }
  };

  HwSpirits.prototype.destroy = function () {
    if (this.timer) clearTimeout(this.timer);
  };

  /* ============================================================== HwDock === */
  /* The reward row sticks once its anchor reaches the top of the viewport.
   * Transcribed from the source: scrollY > 0 AND anchor top <= 0. The scrollY
   * guard matters and cannot be expressed with an IntersectionObserver, which
   * is why this stays on the scroll pump. */
  function HwDock(el, signal) {
    this.anchor = el;
    this.dock = el.querySelector('.hw-reward-dock');
    this.docked = null;
    var self = this;
    this.onScroll = function () { self.sync(); };
    scrollSubs.add(this.onScroll);
    resizeSubs.add(this.onScroll);
    this.sync();
  }
  HwDock.prototype.sync = function () {
    var visible = window.scrollY > 0 && this.anchor.getBoundingClientRect().top <= 0;
    if (visible === this.docked) return;      // avoid touching classList every frame
    this.docked = visible;
    this.anchor.classList.toggle('hw-is-docked', visible);
    if (this.dock) this.dock.classList.toggle('hw-is-visible', visible);
  };
  HwDock.prototype.destroy = function () {
    scrollSubs.delete(this.onScroll);
    resizeSubs.delete(this.onScroll);
    this.anchor.classList.remove('hw-is-docked');
    if (this.dock) this.dock.classList.remove('hw-is-visible');
  };

  /* ================================================= <halloween-countdown> = */
  /* Mirrors the attribute contract of the theme's own <countdown-timer>
   * (sections/countdown.liquid): expires-at as an ISO string with offset, plus
   * expiration-behavior. Liquid pre-renders the real DD/HH/MM/SS from the shop
   * timezone, so there is no 00:00:00 flash; this only keeps them ticking. */
  function pad2(n) { return (n < 10 ? '0' : '') + n; }

  if (!window.customElements.get('halloween-countdown')) {
    window.customElements.define('halloween-countdown', class extends HTMLElement {
      connectedCallback() {
        this.units = {
          days: this.querySelector('[data-hw-unit="days"]'),
          hours: this.querySelector('[data-hw-unit="hours"]'),
          minutes: this.querySelector('[data-hw-unit="minutes"]'),
          seconds: this.querySelector('[data-hw-unit="seconds"]'),
        };
        var raw = this.getAttribute('expires-at');
        var t = raw ? Date.parse(raw) : NaN;
        this.expiresAt = isNaN(t) ? null : t;
        if (this.expiresAt === null) return;   // unparseable: leave the server values
        this.tick();
        this.timer = window.setInterval(this.tick.bind(this), 1000);
      }
      disconnectedCallback() { if (this.timer) clearInterval(this.timer); }
      tick() {
        var left = Math.floor((this.expiresAt - Date.now()) / 1000);
        if (left <= 0) {
          left = 0;
          if (this.timer) { clearInterval(this.timer); this.timer = 0; }
          if (this.getAttribute('expiration-behavior') === 'hide') this.hidden = true;
        }
        var d = Math.floor(left / 86400);
        var h = Math.floor((left % 86400) / 3600);
        var m = Math.floor((left % 3600) / 60);
        var s = left % 60;
        if (this.units.days) this.units.days.textContent = pad2(d);
        if (this.units.hours) this.units.hours.textContent = pad2(h);
        if (this.units.minutes) this.units.minutes.textContent = pad2(m);
        if (this.units.seconds) this.units.seconds.textContent = pad2(s);
      }
    });
  }

  /* ================================================ <halloween-reward-bar> = */
  /* Two spend thresholds driven by the REAL cart. Attribute contract copied
   * from the theme's <free-shipping-bar> (assets/theme.js): thresholds and
   * total-price in cents, observed attributes, cart:change on document.
   *
   * Basis is items_subtotal_price, NOT total_price: total_price is net of
   * cart-level discounts, so earning the $10 reward would push the customer
   * back under the threshold and revoke it on the next update. */
  if (!window.customElements.get('halloween-reward-bar')) {
    window.customElements.define('halloween-reward-bar', class extends HTMLElement {
      static get observedAttributes() { return ['threshold-1', 'threshold-2', 'total-price']; }
      connectedCallback() {
        this.onCartChange = this.onCartChange.bind(this);
        document.addEventListener('cart:change', this.onCartChange);
        document.addEventListener('cart:refresh', this.onCartChange);
        // Same formatter the theme's <free-shipping-bar> uses, so the spoken
        // amount matches the shop's currency and Markets locale.
        try {
          this.money = new Intl.NumberFormat(window.Shopify && Shopify.locale, {
            style: 'currency',
            currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR',
          });
        } catch (e) { this.money = null; }
        this.render();
      }
      disconnectedCallback() {
        document.removeEventListener('cart:change', this.onCartChange);
        document.removeEventListener('cart:refresh', this.onCartChange);
        if (this.pending) { this.pending.abort(); this.pending = null; }
      }
      attributeChangedCallback() { this.render(); }
      onCartChange() {
        // The qualifying total leaves out products tagged #new, and /cart.js
        // line items carry no product tags — so the cart payload in the event
        // cannot answer it. Re-render just this section through the Section
        // Rendering API and adopt ITS total-price: the rule then lives in one
        // place (halloween-countdown.liquid) for first paint and every update.
        var wrap = this.closest('.shopify-section');
        var id = wrap && wrap.id ? wrap.id.replace('shopify-section-', '') : '';
        if (!id) return;
        // A newer cart change supersedes an in-flight one; never let an older
        // response land last and show a stale total.
        if (this.pending) this.pending.abort();
        var ac = this.pending = new AbortController();
        var self = this;
        var url = location.pathname + location.search +
          (location.search ? '&' : '?') + 'sections=' + encodeURIComponent(id);
        fetch(url, { signal: ac.signal, credentials: 'same-origin' })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (data) {
            if (self.pending === ac) self.pending = null;
            var html = data && data[id];
            if (!html) return;
            var bar = new DOMParser().parseFromString(html, 'text/html')
              .querySelector('halloween-reward-bar');
            var total = bar && bar.getAttribute('total-price');
            if (total !== null && total !== undefined && total !== '') {
              self.setAttribute('total-price', total);
            }
          })
          .catch(function () {
            // aborted by a newer change, or offline: keep the last known total
          });
      }
      render() {
        var total = parseFloat(this.getAttribute('total-price')) || 0;
        var meters = this.querySelectorAll('.hw-progress-meter');
        var thresholds = [
          parseFloat(this.getAttribute('threshold-1')) || 0,
          parseFloat(this.getAttribute('threshold-2')) || 0,
        ];
        var remainingMsg = [];
        for (var i = 0; i < meters.length && i < thresholds.length; i++) {
          var t = thresholds[i];
          var p = t > 0 ? Math.min(total / t, 1) : 0;
          var rail = meters[i].querySelector('.hw-meter-rail');
          var fill = meters[i].querySelector('.hw-track > span');
          if (rail) rail.style.setProperty('--hw-progress', String(p));
          if (fill) fill.style.width = (p * 100).toFixed(2) + '%';
          var label = meters[i].querySelector('strong');
          var name = label ? label.textContent.trim() : 'reward ' + (i + 1);
          var short = (t - total) / 100;
          var shortText = this.money ? this.money.format(short) : short.toFixed(2);
          remainingMsg.push(p >= 1
            ? name + ' unlocked'
            : shortText + ' more to unlock ' + name);
        }
        // Mobile presentation: one bar running 0 -> the highest threshold, with
        // a node per milestone. Same numbers, different shape.
        var top = Math.max.apply(null, thresholds.concat([0]));
        var fill = this.querySelector('[data-hw-unlock-fill]');
        var rider = this.querySelector('[data-hw-unlock-rider]');
        if (top > 0) {
          var pct = (Math.min(total / top, 1) * 100).toFixed(2) + '%';
          // The pumpkin rides the leading edge of the fill, so both read
          // from the same percentage — they can never drift apart.
          if (fill) fill.style.width = pct;
          if (rider) rider.style.left = pct;
        }
        var marks = this.querySelectorAll('[data-hw-unlock-node],[data-hw-unlock-label]');
        for (var m = 0; m < marks.length; m++) {
          var at = parseFloat(marks[m].getAttribute('data-hw-unlock-node') ||
                              marks[m].getAttribute('data-hw-unlock-label')) || 0;
          marks[m].classList.toggle('hw-is-unlocked', at > 0 && total >= at);
        }

        var status = this.querySelector('[data-hw-reward-status]');
        if (status) status.textContent = remainingMsg.join('. ');
      }
    });
  }

  /* =========================================================== fitTitles === */
  function fitTitle(h3) {
    var full = h3.getAttribute('data-hw-title');
    if (!full) return;
    var w = h3.clientWidth;
    if (!w) return;                       // hidden / not laid out yet
    // Write into the product link when there is one. Setting h3.textContent
    // directly would delete the <a> wrapper and silently strip the title's
    // link on the very first measurement pass.
    var sink = h3.querySelector('.hw-product-link') || h3;
    var st = window.getComputedStyle(h3);
    measureCtx.font = st.fontStyle + ' ' + st.fontWeight + ' ' + st.fontSize + ' ' + st.fontFamily;
    var ls = parseFloat(st.letterSpacing) || 0;
    function m(v) { return measureCtx.measureText(v).width + Math.max(0, v.length - 1) * ls; }

    if (m(full) <= w) { if (sink.textContent !== full) sink.textContent = full; return; }
    var words = full.trim().split(/\s+/);
    var fit = '';
    for (var i = 0; i < words.length; i++) {
      var cand = fit ? fit + ' ' + words[i] : words[i];
      if (m(cand + '…') > w) break;
      fit = cand;
    }
    var fitted = fit ? fit + '…' : '…';
    if (sink.textContent !== fitted) sink.textContent = fitted;
  }

  function fitTitles(root) {
    var nodes = root.querySelectorAll('h3[data-hw-title]');
    for (var i = 0; i < nodes.length; i++) fitTitle(nodes[i]);
  }

  /* =========================================================== lifecycle === */
  var controllers = new Map();

  function initRoot(root) {
    if (controllers.has(root)) return;
    var ac = new AbortController();
    var parts = [];

    root.querySelectorAll('[data-hw-component="mask"]').forEach(function (el) {
      parts.push(new HwMask(el, ac.signal));
    });
    root.querySelectorAll('[data-hw-component="sequence"]').forEach(function (el) {
      parts.push(new HwSequence(el, ac.signal));
    });
    root.querySelectorAll('[data-hw-component="dock"]').forEach(function (el) {
      parts.push(new HwDock(el, ac.signal));
    });
    root.querySelectorAll('[data-hw-component="spirits"]').forEach(function (el) {
      parts.push(new HwSpirits(el, ac.signal));
    });
    root.querySelectorAll('[data-hw-hero-panel]').forEach(function (el) {
      parts.push(new HwHeroFit(el, ac.signal));
      var heroMask = parts.filter(function (p) {
        return p instanceof HwMask && p.gate && el.contains(p.el);
      })[0];
      if (heroMask) parts.push(new HwHeroRestore(el, heroMask, ac.signal));
    });

    var inView = true;
    function syncMotion() {
      root.classList.toggle('hw-motion-paused', !inView || document.hidden);
    }
    var motionObserver = null;
    if ('IntersectionObserver' in window) {
      motionObserver = new IntersectionObserver(function (entries) {
        inView = entries[0].isIntersecting;
        syncMotion();
        if (inView) {
          // Decode the second scene before it is needed, without eagerly loading
          // every image in sections the visitor has not approached yet.
          root.querySelectorAll('[data-hw-slide] img[loading="lazy"]').forEach(function (img) {
            img.loading = 'eager';
            img.decoding = 'async';
            if (img.decode) img.decode().catch(function () {});
          });
        }
      }, { rootMargin: '200px 0px' });
      motionObserver.observe(root);
    }
    document.addEventListener('visibilitychange', syncMotion, { signal: ac.signal });
    REDUCED.addEventListener('change', function () {
      if (REDUCED.matches) parts.forEach(function (p) {
        if (p instanceof HwMask) p.finish();
      });
    }, { signal: ac.signal });
    syncMotion();

    fitTitles(root);
    var ro = null;
    if ('ResizeObserver' in window) {
      ro = new ResizeObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) fitTitle(entries[i].target);
      });
      root.querySelectorAll('h3[data-hw-title]').forEach(function (h) { ro.observe(h); });
    }
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () { fitTitles(root); });
    }

    controllers.set(root, {
      destroy: function () {
        ac.abort();
        if (ro) ro.disconnect();
        if (motionObserver) motionObserver.disconnect();
        root.classList.remove('hw-motion-paused');
        parts.forEach(function (p) { if (p.destroy) p.destroy(); });
      },
      parts: parts
    });
    pump();
  }

  function destroyIn(container) {
    controllers.forEach(function (ctrl, root) {
      if (container === root || container.contains(root)) {
        ctrl.destroy();
        controllers.delete(root);
      }
    });
  }

  function refresh() {
    document.querySelectorAll('[data-hw-root]').forEach(initRoot);
  }

  /* theme editor -------------------------------------------------------- */
  function sequencesIn(target) {
    var out = [];
    controllers.forEach(function (ctrl, root) {
      if (target.contains(root) || root.contains(target)) {
        ctrl.parts.forEach(function (p) { if (p instanceof HwSequence) out.push(p); });
      }
    });
    return out;
  }

  document.addEventListener('shopify:section:load', function (e) {
    e.target.querySelectorAll('[data-hw-root]').forEach(initRoot);
    if (e.target.matches && e.target.matches('[data-hw-root]')) initRoot(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) { destroyIn(e.target); });
  document.addEventListener('shopify:section:select', function (e) {
    sequencesIn(e.target).forEach(function (s) { s.sync(); });
  });
  document.addEventListener('shopify:block:select', function (e) {
    // Spirit blocks: jump the coverflow to the selected card.
    var card = e.target.closest ? e.target.closest('[data-hw-spirit-index]') : null;
    if (card) {
      var stage = card.closest('[data-hw-component="spirits"]');
      controllers.forEach(function (ctrl, root) {
        if (!stage || (!root.contains(stage) && stage !== root)) return;
        ctrl.parts.forEach(function (p) {
          if (p instanceof HwSpirits) p.goTo(Number(card.getAttribute('data-hw-spirit-index')));
        });
      });
      return;
    }
    // Without this the merchant can never edit the second slide.
    var slide = e.target.closest ? e.target.closest('[data-hw-slide]') : null;
    if (!slide) return;
    var seqEl = slide.closest('[data-hw-component="sequence"]');
    if (!seqEl) return;
    sequencesIn(seqEl).forEach(function (s) {
      var idx = s.slides.indexOf(slide);
      if (idx >= 0) s.pin(idx);
    });
  });
  document.addEventListener('shopify:block:deselect', function (e) {
    var slide = e.target.closest ? e.target.closest('[data-hw-slide]') : null;
    if (!slide) return;
    var seqEl = slide.closest('[data-hw-component="sequence"]');
    if (seqEl) sequencesIn(seqEl).forEach(function (s) { s.unpin(); });
  });

  // Never strand the page: if anything above threw before the gate released.
  window.addEventListener('pagehide', function () { lock.forceRelease(); });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh, { once: true });
  } else {
    refresh();
  }

  window.Halloween = {
    init: initRoot,
    destroy: destroyIn,
    refresh: refresh,
    fitTitles: fitTitles,
    unlock: function () { lock.forceRelease(); },
    mq: { MOBILE: MOBILE, REDUCED: REDUCED, EDITING: EDITING },
    _controllers: controllers,
    // Test hook: drives the subscriber list without waiting for a frame, so the
    // scroll chain can be asserted in environments where rAF is suspended
    // (hidden tabs, headless runs).
    _flush: function () { rafId = 0; flush(); }
  };
})();





